/**
 * Shared options for Checkup and OnboardingWizard (browser-safe).
 * Country-aware intent options, capacity hints — NO database imports.
 * DB-dependent functions live in checkup-options-server.ts.
 */
import { CAPACITY_THRESHOLDS } from "./constants";

type CountryCode = "KE" | "NG" | "ZA" | "GH" | "NA" | "ZM";

// ─── Intent Options ───

interface IntentOption {
  value: string;
  label: string;
}

/**
 * Returns capacity-aware intent options per country.
 * Mirrors OnboardingWizard routing — KE options change at the 1MW threshold.
 */
export function getIntentOptions(country: CountryCode, capacityKw: number): IntentOption[] {
  switch (country) {
    case "KE":
      return capacityKw <= CAPACITY_THRESHOLDS.KE_NET_METERING_MAX
        ? [
            { value: "own_use", label: "Own use (self-consumption)" },
            { value: "sell", label: "Sell to grid (net metering)" },
          ]
        : [
            { value: "grid_sale", label: "Grid sale (PPA with KPLC)" },
            { value: "corporate_sale", label: "Corporate PPA" },
            { value: "own_use", label: "Own use" },
          ];
    case "NG":
      return [
        { value: "grid_sale", label: "On-grid (NBET / bilateral)" },
        { value: "embedded", label: "Embedded generation (C&I)" },
        { value: "mini_grid", label: "Mini-grid" },
        { value: "own_use", label: "Own use / captive" },
      ];
    case "ZA":
      return [
        { value: "private_offtaker", label: "Private offtaker (wheeling / behind-the-meter)" },
        { value: "eskom", label: "Eskom sale (REIPPPP / bilateral)" },
        { value: "municipal", label: "Municipal sale" },
        { value: "own_use", label: "Own use" },
      ];
    case "GH":
      return [
        { value: "grid_sale", label: "Grid sale (ECG / GRIDCo PPA)" },
        { value: "embedded", label: "Embedded generation (C&I)" },
        { value: "own_use", label: "Own use / captive" },
      ];
    case "NA":
      return [
        { value: "grid_sale", label: "Grid sale (NamPower PPA)" },
        { value: "wheeling", label: "Independent / wheeling" },
        { value: "own_use", label: "Own use / captive" },
      ];
    case "ZM":
      return [
        { value: "grid_sale", label: "Grid sale (ZESCO PPA)" },
        { value: "mini_grid", label: "Mini-grid / off-grid" },
        { value: "own_use", label: "Own use / captive" },
      ];
    default:
      return [];
  }
}

// ─── Capacity Hints ───

/**
 * Returns a country-specific capacity threshold hint, or null if none applies.
 */
export function getCapacityHint(country: CountryCode, capacityKw: number): string | null {
  switch (country) {
    case "KE":
      if (capacityKw <= CAPACITY_THRESHOLDS.KE_NET_METERING_MAX)
        return "Projects under 1MW may qualify for net metering or exemption.";
      return null;
    case "NG":
      if (capacityKw <= CAPACITY_THRESHOLDS.NG_REGISTRATION_MAX)
        return "Projects under 100kW only need NERC registration.";
      if (capacityKw <= CAPACITY_THRESHOLDS.NG_MINI_GRID_MAX)
        return "Projects under 1MW qualify for mini-grid permit.";
      return null;
    case "ZA":
      if (capacityKw <= CAPACITY_THRESHOLDS.ZA_SSEG_MAX)
        return "Systems under 100kW may register as SSEG at municipal level.";
      if (capacityKw < 10000)
        return "Projects under 10MW may use the Solar PV Exclusion Norm (faster environmental path).";
      return null;
    case "GH":
      if (capacityKw <= CAPACITY_THRESHOLDS.GH_EMBEDDED_MAX)
        return "Projects under 500kW qualify for simplified Energy Commission permit.";
      return null;
    case "NA":
      if (capacityKw <= CAPACITY_THRESHOLDS.NA_NET_METERING_MAX)
        return "Projects under 500kW qualify for ECB net metering registration.";
      return null;
    case "ZM":
      if (capacityKw < CAPACITY_THRESHOLDS.ZM_LICENSE_EXEMPT_MAX)
        return "Own-use projects under 250kW are license-exempt under the Electricity Act 2019.";
      return null;
    default:
      return null;
  }
}

// ─── Capacity Step ───

/**
 * Returns the appropriate step increment for the capacity input spinner.
 * 0-1 MW: 0.1 steps, 1-5 MW: 0.5 steps, >5 MW: 1.0 steps.
 */
export function getCapacityStep(mw: number): number {
  if (mw < 1) return 0.1;
  if (mw < 5) return 0.5;
  return 1;
}

/**
 * Snaps a MW value to the nearest valid increment for its tier.
 */
export function snapCapacity(mw: number): number {
  if (mw <= 0) return 0.1;
  const step = getCapacityStep(mw);
  return Math.round(mw / step) * step;
}

// ─── Location Placeholders ───

/** Country-specific placeholder text for the project location field. */
export const LOCATION_PLACEHOLDER: Record<CountryCode, string> = {
  KE: "e.g., Nakuru County",
  NG: "e.g., Lagos State",
  ZA: "e.g., Western Cape",
  GH: "e.g., Greater Accra",
  NA: "e.g., Erongo Region",
  ZM: "e.g., Lusaka Province",
};

// ─── Country Form Options (inline, browser-safe) ───
// Mirrors seed data from src/db/seed.ts — no API call needed.

export const EIA_TYPE_OPTIONS: Record<CountryCode, string[]> = {
  KE: ["Full EIA Study", "Comprehensive Project Report (CPR)", "Exempt (own-use <1MW)"],
  NG: ["Full EIA (≥10MW)", "Environmental Management Plan (<10MW)", "Exempt (very small scale)"],
  ZA: ["Full S&EIA (≥20MW)", "Basic Assessment (10-20MW)", "Solar PV Exclusion Norm (<10MW)", "Exempt (<10MW, no listed activities)"],
  GH: ["Full EIA (Schedule 1 — large-scale)", "Preliminary Environmental Report (Schedule 2)", "Environmental Management Plan (small-scale)"],
  NA: ["Full EIA (listed activities)", "Environmental Management Plan", "Scoping Report + EMP"],
  ZM: ["Full EIA (First Schedule projects)", "Environmental Project Brief (Second Schedule)", "Exempt (Third Schedule)"],
};

export const LAND_TENURE_OPTIONS: Record<CountryCode, string[]> = {
  KE: ["Freehold", "Leasehold (max 99yr non-citizen)", "Community Land", "Public Land (NLC allocation)"],
  NG: ["Right of Occupancy (99-year leasehold)", "Customary Land (family/community)", "Government Allocation"],
  ZA: ["Freehold", "Leasehold", "State Land (DRDLR lease)", "Communal (Traditional Authority)"],
  GH: ["Allodial Title (stool/skin land)", "Customary Freehold", "Leasehold (state land, 50yr max for non-citizens)", "Common Law Freehold"],
  NA: ["Freehold", "Leasehold (state land)", "Communal Land (Traditional Authority + Communal Land Board)"],
  ZM: ["Leasehold (state land, 99yr)", "Customary Land (Chief + District Council consent)", "Converted Leasehold (customary → leasehold)"],
};

export const OFFTAKER_OPTIONS: Record<CountryCode, string[]> = {
  KE: ["KPLC (grid sale)", "Corporate PPA", "Own use"],
  NG: ["NBET (on-grid)", "DisCo bilateral", "C&I embedded", "Mini-grid community", "Own use"],
  ZA: ["Private offtaker (wheeling)", "Eskom", "Municipality", "Own use"],
  GH: ["ECG", "GRIDCo", "Corporate", "Own use"],
  NA: ["NamPower", "Merchant (SAPP)", "Own use"],
  ZM: ["ZESCO", "Open Access bilateral", "Own use"],
};

// ─── Country-Specific Conditional Questions ───

export interface CountryQuestion {
  id: string;
  section: "land" | "env" | "offtake";
  label: string;
  options: string[];
}

/** Returns country-specific questions that appear conditionally in the Status step. */
export function getCountryQuestions(country: CountryCode): CountryQuestion[] {
  const landQ: Record<CountryCode, CountryQuestion | null> = {
    KE: { id: "cq-land", section: "land", label: "Is this community land requiring FPIC?", options: ["Yes", "No", "Unknown"] },
    NG: { id: "cq-land", section: "land", label: "Do you have a Certificate of Occupancy?", options: ["Yes", "Applied", "No"] },
    ZA: { id: "cq-land", section: "land", label: "Is the land classified as agricultural?", options: ["Yes", "No", "Unknown"] },
    GH: { id: "cq-land", section: "land", label: "Is this stool/skin land requiring Lands Commission concurrence?", options: ["Yes", "No", "Unknown"] },
    NA: { id: "cq-land", section: "land", label: "Is this communal land requiring Traditional Authority consent?", options: ["Yes", "No"] },
    ZM: { id: "cq-land", section: "land", label: "Is this customary land requiring Chief + District Council consent?", options: ["Yes", "No"] },
  };

  const envQ: Record<CountryCode, CountryQuestion | null> = {
    KE: null,
    NG: { id: "cq-env", section: "env", label: "Have you pre-consulted TCN on grid evacuation capacity?", options: ["Yes", "No", "N/A (off-grid)"] },
    ZA: { id: "cq-env", section: "env", label: "Have you completed Site Sensitivity Verification for the Exclusion Norm?", options: ["Yes", "No", "Not applicable"] },
    GH: { id: "cq-env", section: "env", label: "Is your grid connection via GRIDCo (transmission) or ECG (distribution)?", options: ["GRIDCo (>10MW)", "ECG (<10MW)", "Unsure"] },
    NA: null,
    ZM: { id: "cq-env", section: "env", label: "Have you initiated the Ministerial feasibility study approval?", options: ["Yes", "No"] },
  };

  const offtakeQ: Record<CountryCode, CountryQuestion | null> = {
    KE: null, NG: null, ZA: null, NA: null, ZM: null,
    GH: { id: "cq-offtake", section: "offtake", label: "Have you assessed ECG offtaker creditworthiness risk?", options: ["Yes, aware and mitigated", "Aware of risk", "No"] },
  };

  return [landQ[country], envQ[country], offtakeQ[country]].filter((q): q is CountryQuestion => q !== null);
}

